import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import DemoBadge from "./DemoBadge";
import { mediaSrc } from "../lib/media";
import { t, formatDate } from "../lib/i18n";

export const SectionHead = ({ label, title, children, action, as: Tag = "h2" }) => (
  <div className="flex flex-wrap items-end justify-between gap-6 mb-10 lg:mb-16">
    <div>
      {label && <div className="vb-label mb-3">{label}</div>}
      <Tag className="vb-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{title}</Tag>
      {children && <p className="mt-4 text-sm md:text-base text-neutral-400 max-w-2xl">{children}</p>}
    </div>
    {action}
  </div>
);

export const PersonCard = ({ person, testid }) => (
  <Link
    to={person.slug ? `/createurs/${person.slug}` : "#"}
    data-testid={testid}
    className="group block relative overflow-hidden vb-card"
  >
    <div className="aspect-[3/4] overflow-hidden bg-[#0d0d0d]">
      {person.profile_photo_url ? (
        <img
          src={mediaSrc(person.profile_photo_url)}
          alt={person.display_name}
          loading="lazy"
          className="w-full h-full object-cover vb-zoom"
        />
      ) : (
        <div className="w-full h-full grid place-items-center text-neutral-700 vb-display text-4xl">
          {(person.display_name || "?").charAt(0)}
        </div>
      )}
    </div>
    <div className="absolute inset-x-0 bottom-0 p-5 vb-scrim">
      <div className="flex items-center gap-2 mb-2">
        <DemoBadge show={person.is_demo} />
      </div>
      <div className="vb-display text-xl font-semibold">{person.display_name}</div>
      <div className="text-xs text-neutral-400 mt-1">
        {(person.profile_types || []).map((p) => t(`profile_types.${p}`, p)).join(" · ")}
        {person.city ? ` · ${person.city}` : ""}
      </div>
    </div>
  </Link>
);

export const MediaTile = ({ item, testid, className = "" }) => {
  const poster = item.poster_url || item.thumbnail_url;
  return (
    <div data-testid={testid} className={`group relative overflow-hidden bg-[#0a0a0a] ${className}`}>
      {item.type === "video" ? (
        poster ? (
          <video
            src={mediaSrc(item.url)}
            poster={mediaSrc(poster)}
            preload="none"
            controls
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <video
            src={mediaSrc(item.url)}
            preload="none"
            controls
            playsInline
            data-testid={testid ? `${testid}-fallback` : undefined}
            className="w-full h-full object-cover bg-gradient-to-br from-[#1a1013] via-[#0a0a0a] to-[#050505]"
          />
        )
      ) : (
        <img
          src={mediaSrc(item.thumbnail_url || item.url)}
          alt={item.caption || "Média Vytali"}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover vb-zoom"
        />
      )}
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <DemoBadge show={item.is_demo} />
        {item.type === "video" && (
          <span className="inline-flex items-center gap-1 bg-black/70 text-white text-[10px] uppercase tracking-widest px-2 py-1">
            <Play size={10} /> {t("common.video")}
          </span>
        )}
      </div>
      {item.type === "video" && !poster && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
          <span className="h-12 w-12 rounded-full border border-white/30 grid place-items-center text-white/80">
            <Play size={16} />
          </span>
          {item.caption && (
            <span className="px-4 text-center text-[11px] text-neutral-400 line-clamp-2">{item.caption}</span>
          )}
        </div>
      )}
    </div>
  );
};

export const EventCard = ({ event, testid }) => (
  <Link to={`/evenements/${event.slug}`} data-testid={testid} className="group block vb-card overflow-hidden">
    <div className="aspect-[16/10] overflow-hidden bg-[#0d0d0d]">
      {event.hero_media_url && (
        <img src={mediaSrc(event.hero_media_url)} alt={event.name} loading="lazy" className="w-full h-full object-cover vb-zoom" />
      )}
    </div>
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="vb-label !text-[#FF2E63]">{event.event_type}</span>
        <DemoBadge show={event.is_demo} />
      </div>
      <div className="vb-display text-xl lg:text-2xl font-semibold">{event.name}</div>
      <div className="mt-2 text-xs text-neutral-500">
        {formatDate(event.date_time)} {event.venue ? `· ${event.venue}` : ""}
      </div>
    </div>
  </Link>
);

export const PartnerCard = ({ partner, testid }) => (
  <Link to={`/partenaires/${partner.slug}`} data-testid={testid} className="group block relative overflow-hidden vb-card">
    <div className="aspect-[4/5] overflow-hidden bg-[#0d0d0d]">
      {partner.hero_media_url && (
        <img src={mediaSrc(partner.hero_media_url)} alt={partner.business_name} loading="lazy" className="w-full h-full object-cover vb-zoom" />
      )}
    </div>
    <div className="absolute inset-x-0 bottom-0 p-6 vb-scrim">
      <div className="flex items-center gap-2 mb-2">
        <span className="vb-label">{partner.category}</span>
        <DemoBadge show={partner.is_demo} />
      </div>
      <div className="vb-display text-2xl font-semibold">{partner.business_name}</div>
      <p className="mt-2 text-sm text-neutral-400 line-clamp-2">{partner.atmosphere_description || partner.short_description}</p>
    </div>
  </Link>
);

export const ChallengeCard = ({ challenge, testid }) => (
  <Link to={`/challenges/${challenge.slug}`} data-testid={testid} className="group block vb-card overflow-hidden">
    <div className="aspect-[16/9] overflow-hidden bg-[#0d0d0d]">
      {challenge.hero_media_url && (
        <img src={mediaSrc(challenge.hero_media_url)} alt={challenge.title} loading="lazy" className="w-full h-full object-cover vb-zoom" />
      )}
    </div>
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="vb-label !text-[#FF2E63]">{challenge.status === "open" ? "En cours" : "Fermé"}</span>
        <DemoBadge show={challenge.is_demo} />
      </div>
      <div className="vb-display text-xl lg:text-2xl font-semibold">{challenge.title}</div>
      <p className="mt-2 text-sm text-neutral-400 line-clamp-2">{challenge.description}</p>
    </div>
  </Link>
);

export const EmptyState = ({ message, testid = "empty-state" }) => (
  <div data-testid={testid} className="border border-dashed border-white/10 p-12 text-center text-sm text-neutral-500">
    {message || t("common.empty")}
  </div>
);

export const Loader = ({ testid = "loading-state" }) => (
  <div data-testid={testid} className="py-24 text-center text-sm text-neutral-500">
    {t("common.loading")}
  </div>
);
